import dayjs, { Dayjs } from 'dayjs'
import { nextOccurrenceOnOrAfter, occurrencesBetween } from '../../../../utils/rrule'

type DatedEvent = { startDateTime: string; rrule?: string | null }

/**
 * The events of each day ('YYYY-MM-DD'), for the dots of the calendar. A
 * repeating event is filed under each of its occurrences, from the start of
 * the current month to twelve months ahead.
 */
export const buildEventsByDate = <T extends DatedEvent>(
  events: T[],
  now: Dayjs = dayjs(),
): Record<string, T[]> => {
  const byDate: Record<string, T[]> = {}
  const add = (date: Dayjs, event: T) => {
    const key = date.format('YYYY-MM-DD')
    byDate[key] = [...(byDate[key] || []), event]
  }

  for (const event of events) {
    // Plain parse: an ISO date (or a 'YYYY-MM-DD' birth date) read with a
    // format string gave 'Invalid Date' on phones, so no day had a dot.
    const start = dayjs(event.startDateTime)
    const occurrences = event.rrule?.trim()
      ? occurrencesBetween(
          event.rrule,
          start.toDate(),
          now.startOf('month').toDate(),
          now.add(12, 'month').toDate(),
        )
      : []
    if (occurrences.length === 0) add(start, event)
    else occurrences.forEach((occurrence) => add(dayjs(occurrence), event))
  }
  return byDate
}

/**
 * The start to show for an event in the list: its own start, or for a
 * repeating one its occurrence on `day` (the day tapped in the calendar),
 * otherwise its next occurrence from today. Falls back to the first date of
 * the series when there is none.
 */
export const occurrenceStart = (event: DatedEvent, day?: Dayjs | null): string => {
  if (!event.rrule?.trim()) return event.startDateTime
  const next = nextOccurrenceOnOrAfter(
    event.rrule,
    new Date(event.startDateTime),
    (day ?? dayjs()).startOf('day').toDate(),
  )
  if (!next || (day && dayjs(next).isAfter(day, 'day'))) return event.startDateTime
  return next.toISOString()
}

/**
 * The rendez-vous of the day tapped in the calendar. `ofDay` is the server's
 * answer for that day and `all` the whole list. The server repeats a series
 * on its own clock (UTC): around midnight, or once the clock has changed, it
 * files an occurrence under the neighbouring day. So the repeating ones are
 * decided here, on the user's clock, like their dots.
 */
export const eventsOfDay = <T extends DatedEvent & { id: string }>(
  ofDay: T[],
  all: T[],
  day: Dayjs,
): T[] => {
  const repeatsOn = (event: T) => {
    if (!event.rrule?.trim()) return false
    const next = nextOccurrenceOnOrAfter(
      event.rrule,
      new Date(event.startDateTime),
      day.startOf('day').toDate(),
    )
    return !!next && dayjs(next).isSame(day, 'day')
  }
  return [
    ...ofDay.filter((event) => !event.rrule?.trim() || repeatsOn(event)),
    ...all.filter((event) => repeatsOn(event) && !ofDay.some(({ id }) => id === event.id)),
  ]
}
